// Arithmetic shared by the modules' Summary tabs: grouping, counting, summing,
// month buckets and date windows over plain records. Pure, so each module's
// analytics can build on it and every rule has a test.
//
// Dates are the app's ISO strings (yyyy-MM-dd); anything else is treated as
// "no date" and left out of date-based figures rather than guessed at.

const ISO = /^(\d{4})-(\d{2})-(\d{2})/;

/** A number from whatever a record stores (number, "1,200", "", null). */
export const amountOf = (v: unknown): number => {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

/** yyyy-MM-dd of a Date, in local time. */
export const isoOf = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** The date part of a stored value, or null when it is not an ISO date. */
export const dateOf = (v: unknown): string | null => {
  const m = ISO.exec(String(v ?? ''));
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

/** yyyy-MM of a stored date, or null. */
export const monthOf = (v: unknown): string | null => {
  const d = dateOf(v);
  return d ? d.slice(0, 7) : null;
};

const addDays = (d: Date, days: number): Date => {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setDate(out.getDate() + days);
  return out;
};

const text = (v: unknown, blank: string): string => {
  const s = String(v ?? '').trim();
  return s || blank;
};

export interface NamedCount { name: string; count: number; }
export interface NamedTotal { name: string; count: number; total: number; }
export interface MonthBucket { month: string; label: string; count: number; total: number; }

/**
 * How many rows share each value of a field, largest first (ties by name).
 * A blank value is counted under `blank` so nothing silently disappears.
 */
export const countBy = <T>(rows: T[], pick: (r: T) => unknown, blank = 'Not set'): NamedCount[] => {
  const map = new Map<string, number>();
  rows.forEach((r) => {
    const k = text(pick(r), blank);
    map.set(k, (map.get(k) || 0) + 1);
  });
  return Array.from(map, ([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
};

/** Count and sum of an amount per value of a field, largest total first. */
export const totalBy = <T>(
  rows: T[], pick: (r: T) => unknown, amount: (r: T) => unknown, blank = 'Not set'
): NamedTotal[] => {
  const map = new Map<string, NamedTotal>();
  rows.forEach((r) => {
    const k = text(pick(r), blank);
    const cur = map.get(k) || { name: k, count: 0, total: 0 };
    cur.count += 1;
    cur.total += amountOf(amount(r));
    map.set(k, cur);
  });
  return Array.from(map.values())
    .map((x) => ({ ...x, total: Math.round(x.total * 100) / 100 }))
    .sort((a, b) => b.total - a.total || b.count - a.count || a.name.localeCompare(b.name));
};

/** Sum of an amount over rows. */
export const sumOf = <T>(rows: T[], amount: (r: T) => unknown): number =>
  Math.round(rows.reduce((s, r) => s + amountOf(amount(r)), 0) * 100) / 100;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * The last `span` calendar months up to and including this one, oldest first,
 * each with how many rows fell in it and (optionally) their total. Months with
 * nothing are kept, so a quiet month shows as a gap rather than vanishing.
 */
export const lastMonths = <T>(
  rows: T[], date: (r: T) => unknown, span = 12, now: Date = new Date(), amount?: (r: T) => unknown
): MonthBucket[] => {
  const buckets: MonthBucket[] = [];
  for (let i = span - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    buckets.push({ month, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}`, count: 0, total: 0 });
  }
  const index = new Map(buckets.map((b, i) => [b.month, i]));
  rows.forEach((r) => {
    const i = index.get(monthOf(date(r)) || '');
    if (i === undefined) return;
    buckets[i].count += 1;
    if (amount) buckets[i].total += amountOf(amount(r));
  });
  return buckets.map((b) => ({ ...b, total: Math.round(b.total * 100) / 100 }));
};

/** Rows dated from today to `days` ahead (inclusive), soonest first. */
export const upcoming = <T>(rows: T[], date: (r: T) => unknown, days = 30, now: Date = new Date()): T[] => {
  const from = isoOf(now);
  const to = isoOf(addDays(now, days));
  return rows
    .filter((r) => { const d = dateOf(date(r)); return d !== null && d >= from && d <= to; })
    .sort((a, b) => String(dateOf(date(a))).localeCompare(String(dateOf(date(b)))));
};

/** Rows dated in the last `days` days up to today (inclusive), newest first. */
export const recent = <T>(rows: T[], date: (r: T) => unknown, days = 30, now: Date = new Date()): T[] => {
  const from = isoOf(addDays(now, -days));
  const to = isoOf(now);
  return rows
    .filter((r) => { const d = dateOf(date(r)); return d !== null && d >= from && d <= to; })
    .sort((a, b) => String(dateOf(date(b))).localeCompare(String(dateOf(date(a)))));
};

/** Rows dated in the given calendar year. */
export const inYear = <T>(rows: T[], date: (r: T) => unknown, year: number): T[] =>
  rows.filter((r) => (dateOf(date(r)) || '').startsWith(`${year}-`));

/** Rows dated in the current calendar month. */
export const inMonth = <T>(rows: T[], date: (r: T) => unknown, now: Date = new Date()): T[] => {
  const m = isoOf(now).slice(0, 7);
  return rows.filter((r) => monthOf(date(r)) === m);
};

/** Whole days from today until a date (negative when past), or null with no date. */
export const daysUntil = (v: unknown, now: Date = new Date()): number | null => {
  const d = dateOf(v);
  if (!d) return null;
  const [y, m, day] = d.split('-').map(Number);
  const target = new Date(y, m - 1, day).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((target - today) / 86_400_000);
};

/** Completed years between a date and today (an age, a length of service), or null. */
export const yearsSince = (v: unknown, now: Date = new Date()): number | null => {
  const d = dateOf(v);
  if (!d) return null;
  const [y, m, day] = d.split('-').map(Number);
  let years = now.getFullYear() - y;
  if (now.getMonth() + 1 < m || (now.getMonth() + 1 === m && now.getDate() < day)) years -= 1;
  return years;
};

/** The largest value of a field across rows — the 100% mark for a bar column. */
export const peakOf = <T>(rows: T[], value: (r: T) => number): number =>
  rows.reduce((max, r) => Math.max(max, value(r) || 0), 0);

/** A whole-number percentage of part in whole; 0 when whole is 0. */
export const percent = (part: number, whole: number): number =>
  whole > 0 ? Math.round((part / whole) * 100) : 0;
